import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/**
 * ✅ Unpkg CDN ensures worker version directly matches pdfjs version
 */
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function BrochurePdfViewer({ file, title = "Brochure" }) {
    const wrapRef = useRef(null);

    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    const [containerWidth, setContainerWidth] = useState(360);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        if (!wrapRef.current) return;
        const el = wrapRef.current;

        const ro = new ResizeObserver(() => {
            const w = el.getBoundingClientRect().width;
            setContainerWidth(Math.max(280, Math.floor(w)));
        });

        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    const onDocumentLoadError = (err) => {
        console.error("PDF load error:", err);
    };

    const pageWidth = useMemo(() => Math.floor(containerWidth), [containerWidth]);

    const canPrev = pageNumber > 1;
    const canNext = numPages ? pageNumber < numPages : false;

    return (
        <section className="w-full">
            <div className="container mx-auto px-[16px] md:px-[24px]">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="text-[16px] md:text-[18px] font-semibold text-[#231F20]">
                                {title}
                            </h2>

                            <div className="text-[12px] text-gray-500 md:hidden">
                                {numPages ? `${pageNumber} / ${numPages}` : ""}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => canPrev && setPageNumber((p) => p - 1)}
                                disabled={!canPrev}
                                className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Prev
                            </button>

                            <button
                                type="button"
                                onClick={() => canNext && setPageNumber((p) => p + 1)}
                                disabled={!canNext}
                                className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>

                            <div className="hidden md:block text-sm text-gray-500 px-2">
                                {numPages ? `${pageNumber} / ${numPages}` : "Loading…"}
                            </div>

                            <div className="flex items-center gap-2 ml-auto">
                                <button
                                    type="button"
                                    onClick={() => setScale((s) => Math.max(0.7, +(s - 0.1).toFixed(1)))}
                                    className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm"
                                >
                                    -
                                </button>

                                <div className="text-sm text-gray-600 w-[56px] text-center">
                                    {Math.round(scale * 100)}%
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setScale((s) => Math.min(2.0, +(s + 0.1).toFixed(1)))}
                                    className="px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm"
                                >
                                    +
                                </button>
                            </div>

                            {typeof file === "string" && (
                                <a
                                    href={file}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-2 rounded-xl bg-[#9e110c] text-white text-sm hover:opacity-90"
                                >
                                    Open PDF
                                </a>
                            )}
                        </div>
                    </div>

                    <div ref={wrapRef} className="p-3 md:p-4 bg-gray-50">
                        <div className="w-full flex justify-center">
                            <Document
                                file={file}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={<div className="py-10 text-center text-gray-500 text-sm">Loading PDF…</div>}
                                error={<div className="py-10 text-center text-red-500 text-sm">Failed to load PDF.</div>}
                            >
                                <Page
                                    key={`page_${pageNumber}_${scale}_${pageWidth}`}
                                    pageNumber={pageNumber}
                                    width={pageWidth * scale}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                />
                            </Document>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}